import React, { useState, useEffect } from 'react';
import { AlertCircle, IndianRupee, Calendar, Search, ChevronDown, ChevronUp } from 'lucide-react';

const EmployeeShortages = () => {
  const [fromDate, setFromDate] = useState(
    new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0]
  );
  const [toDate, setToDate] = useState(new Date().toISOString().split('T')[0]);
  const [minShortage, setMinShortage] = useState(40);
  const [filterOperator, setFilterOperator] = useState('>=');

  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  
  const [expandedEmployee, setExpandedEmployee] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState('All');

  const fetchData = async () => {
    setIsLoading(true);
    setError('');
    setExpandedEmployee(null); // reset expansion
    try {
      const queryParams = new URLSearchParams({ fromDate, toDate });
      const res = await fetch(`/api/employees/shortages/report?${queryParams.toString()}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const json = await res.json();
      if (json.error) {
        setError(json.error);
        setData([]);
      } else {
        setData(json);
      }
    } catch (err) {
      setError(err.message);
      setData([]);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, [fromDate, toDate]);

  // Process data employee-wise
  const filteredData = data.filter(d => {
    const val = parseFloat(d.shortage || 0);
    switch(filterOperator) {
      case '>=': return val >= minShortage;
      case '>': return val > minShortage;
      case '==': return val === minShortage;
      case '<=': return val <= minShortage;
      case '<': return val < minShortage;
      default: return val >= minShortage;
    }
  });
  const employeeWiseData = filteredData.reduce((acc, curr) => {
    if (!acc[curr.employee_name]) {
      acc[curr.employee_name] = { 
        name: curr.employee_name, 
        role: curr.role || 'Unknown',
        incidents: 0, 
        totalShortage: 0, 
        records: [] 
      };
    }
    acc[curr.employee_name].incidents += 1;
    acc[curr.employee_name].totalShortage += parseFloat(curr.shortage || 0);
    acc[curr.employee_name].records.push(curr);
    return acc;
  }, {});

  const uniqueEmployees = Object.keys(employeeWiseData).sort();

  const employeeSummary = Object.values(employeeWiseData)
    .filter(emp => selectedEmployee === 'All' || emp.name === selectedEmployee)
    .sort((a, b) => b.totalShortage - a.totalShortage);
    
  const totalShortage = employeeSummary.reduce((acc, curr) => acc + curr.totalShortage, 0);

  const toggleExpand = (empName) => {
    if (expandedEmployee === empName) {
      setExpandedEmployee(null);
    } else {
      setExpandedEmployee(empName);
    }
  };

  return (
    <div className="space-y-6 max-w-5xl mx-auto pb-12">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <IndianRupee className="w-6 h-6 text-red-600" /> Employee Shortages
          </h1>
          <p className="text-slate-500 text-sm mt-1">View staff cash shortage details between dates.</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 bg-white px-3 py-1 border border-slate-200 rounded-xl">
            <Calendar className="w-4 h-4 text-slate-400" />
            <input 
              type="date" 
              className="outline-none py-1 text-sm text-slate-700 bg-transparent" 
              value={fromDate} 
              onChange={(e) => setFromDate(e.target.value)} 
            />
            <span className="text-slate-300">to</span>
            <input 
              type="date" 
              className="outline-none py-1 text-sm text-slate-700 bg-transparent" 
              value={toDate} 
              onChange={(e) => setToDate(e.target.value)} 
            />
          </div>
          <div className="flex items-center gap-2 bg-white px-3 py-1 border border-slate-200 rounded-xl">
            <select 
              className="outline-none py-1 text-sm text-slate-700 bg-transparent"
              value={selectedEmployee}
              onChange={(e) => {
                const val = e.target.value;
                setSelectedEmployee(val);
                setExpandedEmployee(val !== 'All' ? val : null);
              }}
            >
              <option value="All">All Employees</option>
              {uniqueEmployees.map(emp => (
                <option key={emp} value={emp}>{emp}</option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-1 bg-white px-2 py-1 border border-slate-200 rounded-xl">
            <select
              className="outline-none py-1 text-sm text-slate-600 bg-transparent cursor-pointer font-medium"
              value={filterOperator}
              onChange={(e) => setFilterOperator(e.target.value)}
              title="Filter condition"
            >
              <option value=">=">&ge;</option>
              <option value=">">&gt;</option>
              <option value="==">=</option>
              <option value="<=">&le;</option>
              <option value="<">&lt;</option>
            </select>
            <span className="text-sm text-slate-400">₹</span>
            <input 
              type="number"
              className="outline-none py-1 text-sm text-slate-700 bg-transparent w-14 font-medium"
              value={minShortage}
              onChange={(e) => setMinShortage(Number(e.target.value) || 0)}
              min="0"
              title="Shortage Amount"
            />
          </div>
          <button onClick={fetchData} disabled={isLoading} className="btn-primary py-2 px-4 flex items-center gap-2">
            <Search className="w-4 h-4"/> Fetch
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 text-red-600 p-4 rounded-xl flex items-center gap-3">
          <AlertCircle className="w-5 h-5" />
          <p className="font-medium">{error}</p>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white border border-slate-200 p-5 rounded-2xl shadow-sm flex flex-col justify-center">
          <p className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-1">Total Shortage Amount</p>
          <p className="text-3xl font-extrabold text-red-600">₹{totalShortage.toFixed(2)}</p>
        </div>
        <div className="bg-white border border-slate-200 p-5 rounded-2xl shadow-sm flex flex-col justify-center">
          <p className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-1">Staff With Shortages</p>
          <p className="text-3xl font-extrabold text-slate-800">{employeeSummary.length}</p>
        </div>
        <div className="bg-white border border-slate-200 p-5 rounded-2xl shadow-sm flex flex-col justify-center">
          <p className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-1">Total Incidents</p>
          <p className="text-3xl font-extrabold text-slate-800">{data.length}</p>
        </div>
      </div>

      {/* Employee-Wise Data Table */}
      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden mt-6">
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead className="bg-slate-50 border-b border-slate-200 text-slate-700 uppercase font-bold text-xs">
              <tr>
                <th className="px-6 py-4">Employee Name</th>
                <th className="px-6 py-4 text-center">Incidents</th>
                <th className="px-6 py-4 text-right text-red-600">Total Shortage Amount (₹)</th>
                <th className="px-6 py-4 text-center">Details</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 font-medium">
              {isLoading ? (
                <tr>
                  <td colSpan="4" className="px-6 py-8 text-center text-slate-500 animate-pulse">Loading data...</td>
                </tr>
              ) : employeeSummary.length === 0 ? (
                <tr>
                  <td colSpan="4" className="px-6 py-8 text-center text-slate-500">No shortages found in this date range.</td>
                </tr>
              ) : (
                employeeSummary.map((emp) => (
                  <React.Fragment key={emp.name}>
                    <tr 
                      className={`hover:bg-slate-50 transition-colors cursor-pointer ${expandedEmployee === emp.name ? 'bg-blue-50/30' : ''}`}
                      onClick={() => toggleExpand(emp.name)}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <span className="text-slate-900 font-bold">{emp.name}</span>
                          <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider ${emp.role?.toLowerCase() === 'manager' ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-200 text-slate-600'}`}>
                            {emp.role}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center text-slate-600">
                        <span className="bg-slate-100 text-slate-600 px-2.5 py-1 rounded-lg text-xs font-bold">
                          {emp.incidents}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-red-600 font-extrabold text-base">
                        {emp.totalShortage.toFixed(2)}
                      </td>
                      <td className="px-6 py-4 text-center">
                        <button className="text-slate-400 hover:text-primary-600 transition-colors">
                          {expandedEmployee === emp.name ? <ChevronUp className="w-5 h-5 inline" /> : <ChevronDown className="w-5 h-5 inline" />}
                        </button>
                      </td>
                    </tr>
                    
                    {/* Expanded details row */}
                    {expandedEmployee === emp.name && (
                      <tr className="bg-slate-50/50 border-b border-slate-200">
                        <td colSpan="4" className="px-0 py-0">
                          <div className="px-6 py-4 pl-12 bg-slate-50 border-l-4 border-primary-500">
                            <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Shortage History for {emp.name}</h4>
                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
                              {[...emp.records]
                                .sort((a, b) => new Date(a.business_date) - new Date(b.business_date))
                                .map((record, idx) => (
                                <div key={idx} className="bg-white border border-slate-200 p-3 rounded-xl flex justify-between items-center shadow-sm hover:border-slate-300 transition-colors">
                                  <div className="flex items-center gap-2 text-slate-700 font-medium">
                                    <Calendar className="w-4 h-4 text-slate-400" />
                                    <span>{new Date(record.business_date).toLocaleDateString('en-GB')}</span>
                                  </div>
                                  <span className="font-bold text-red-600">₹{parseFloat(record.shortage).toFixed(2)}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              )}
            </tbody>
            {!isLoading && employeeSummary.length > 0 && (
              <tfoot className="bg-slate-100 border-t-2 border-slate-200 font-bold">
                <tr>
                  <td colSpan="2" className="px-6 py-4 text-right text-slate-700 uppercase tracking-wider">Grand Total:</td>
                  <td className="px-6 py-4 text-right text-red-600 text-xl font-extrabold">₹{totalShortage.toFixed(2)}</td>
                  <td></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
};

export default EmployeeShortages;
